import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';

type ClubAddressFormValues = {
  longitude: string;
  latitude: string;
  postalCode: string;
  businessType: string;
  city: string;
  street: string;
  buildingNumber: string;
  room: string;
  [key: string]: string;
};

type ClubAddressProps = {
  prefectureFromApi?: string;
  premiumStoreId?: string;
};

export const ClubAddress = ({ prefectureFromApi, premiumStoreId }: ClubAddressProps): ReactElement => {
  const { register, setValue } = useFormContext<ClubAddressFormValues>();
  //prefecture is disabled on edit mode
  const isDisabled = !!premiumStoreId;
  const { data: prefectureOptions = [] } = useDropdown('location') || {};
  const { t } = useDynamicLocale();

  return (
    <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20">
      <div className="w-[200px] xl:w-[245px] shrink-0 ">
        <p className="text-base font-semibold  pl-12">
          <Translation id="storeManagement.premiumStores.manageStoreInformation.fieldTitle.clubAddress" />
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <Input label="configurationManagement.location.page.editModal.latitude" {...register('latitude')} />
        <Input label="configurationManagement.location.page.editModal.longitude" {...register('longitude')} />
        <Input
          label="storeManagement.basicStores.manageStoreInfo.clubAddress.postalCode"
          placeholder={t('common.form.placeholder.postalCode')}
          forceRequired
          {...register('postalCode')}
          maxLength={8}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value;

            // keep digits only
            const digits = value.replace(/\D/g, '');

            // max 7 digits
            if (digits.length > 7) return;

            // format as XXX-XXXX
            if (digits.length <= 3) {
              value = digits;
            } else {
              value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
            }

            setValue('postalCode', value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        />
        <Select
          options={prefectureOptions}
          selected={prefectureFromApi}
          disabled={isDisabled}
          forceRequired
          placeholder={t('common.form.placeholder.select')}
          label="storeManagement.basicStores.manageStoreInfo.clubAddress.prefecture"
          {...register('prefecture')}
        ></Select>

        <Input
          label="storeManagement.basicStores.manageStoreInfo.clubAddress.city"
          forceRequired
          placeholder={t('common.form.placeholder.city')}
          {...register('city')}
          maxLength={50}
        />
        <Input
          label="storeManagement.basicStores.manageStoreInfo.clubAddress.street"
          placeholder={t('common.form.placeholder.street')}
          forceRequired
          {...register('street')}
          maxLength={50}
        />
        <Input
          label="storeManagement.basicStores.manageStoreInfo.clubAddress.buildingAndRoomNo"
          placeholder={t('common.form.placeholder.buildingAndRoom')}
          {...register('buildingNo')}
          maxLength={50}
        />
      </div>
    </div>
  );
};
